import React, { useEffect, useState } from 'react';
import { Mail, X } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../utils/api';
import { useAuth } from '../App';

/**
 * Shown above the dashboard when the logged-in user hasn't yet clicked the
 * verification link in the email we sent them at signup. Dismissable for the
 * session but reappears on next mount (until verified server-side).
 *
 * Auto-refreshes user state when the tab regains focus, so users who verify
 * in a separate tab/window see the banner disappear when they switch back.
 */
export default function EmailVerifyBanner({ user, onResend }) {
  const [busy, setBusy] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const { refreshUser } = useAuth() || {};

  // Re-fetch user when the tab regains focus — catches the case where the
  // user clicks the verification link in a new tab/window and returns here.
  useEffect(() => {
    if (!refreshUser) return;
    if (!user || user.email_verified !== false) return;
    const onFocus = () => {
      refreshUser().catch(() => {});
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') onFocus();
    };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [user, refreshUser]);

  if (!user || user.email_verified !== false || dismissed) return null;

  const resend = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.resendVerification();
      toast.success(`Verification email sent to ${user.email}`);
      if (onResend) onResend();
    } catch (e) {
      toast.error(e?.message || 'Could not resend verification email');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="w-full bg-amber-100 dark:bg-amber-500/15 border-b border-amber-300 dark:border-amber-500/30 text-amber-900 dark:text-amber-100 px-4 py-3 flex items-center gap-3"
      data-testid="email-verify-banner"
    >
      <Mail className="w-4 h-4 shrink-0" />
      <p className="text-sm flex-1 leading-snug">
        Please verify <strong className="font-semibold">{user.email}</strong> to keep your account —
        unverified accounts are deleted after 72 hours. Check your inbox for the link.
      </p>
      <button
        onClick={resend}
        disabled={busy}
        className="text-xs font-semibold px-3 py-1.5 rounded-md bg-amber-500 hover:bg-amber-600 text-white border border-amber-600 dark:bg-amber-400/20 dark:hover:bg-amber-400/30 dark:text-amber-100 dark:border-amber-400/30 disabled:opacity-50 transition-colors"
        data-testid="email-verify-resend"
      >
        {busy ? 'Sending…' : 'Resend'}
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="text-amber-900/60 hover:text-amber-900 dark:text-amber-100/60 dark:hover:text-amber-100 transition-colors"
        aria-label="Dismiss"
        data-testid="email-verify-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
